import {otsBaseApi} from "../../ots-api";
import {containerLevelApi} from "../container-level/container-level-store";
import {indexesOf, isActive} from "../../../util/util";
import {adjustFromTimeZoneSafely} from "../../../date/date";
import {Restriction, RestrictionSearchCriteria} from "../../../model/restriction";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ['Restrictions', 'ObjectRestrictions']})

const restrictionApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchRestrictions: builder.query<
      Restriction[],
      RestrictionSearchCriteria
    >({
      query: (criteria) => ({
        url: `/restrictions/search`,
        method: 'POST',
        body: criteria,
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Restrictions", id} as const)),
            {type: "Restrictions", id: "LIST"}
          ];
        } else {
          return [{type: "Restrictions", id: "LIST"}];
        }
      },
    }),
    getAllRestrictions: builder.query<Restriction[], string | undefined>({
      query: (regionId) => ({
        url: `/restrictions/all`,
        method: 'GET',
        params: regionId ? {regionId: regionId} : undefined
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: 'Restrictions', id} as const)),
            {type: 'Restrictions', id: 'LIST'},
          ];
        } else {
          return [{type: 'Restrictions', id: 'LIST'}];
        }
      },
    }),
    getRestrictionsByObject: builder.query<
      Restriction[], { objectId: string }
    >({
      query: (arg) => ({
        url: `/restrictions/search`,
        method: 'POST',
        body: {
          objectIds: [arg.objectId],
          objectHierarchy: true,
        },
      }),
      providesTags: (result, _error, arg, _meta) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Restrictions", id} as const)
            ),
            {type: "ObjectRestrictions", id: arg.objectId}
          ];
        } else {
          return [];
        }
      }
    }),
    getRestriction: builder.query<Restriction, string>({
      query: (id) => ({
        url: `/restrictions/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{type: 'Restrictions', id}],
    }),
    createRestriction: builder.mutation<Restriction, Partial<Restriction>>({
      query: (restriction) => ({
        url: '/restrictions',
        method: 'POST',
        body: restriction,
      }),
      invalidatesTags: (_result, _error, {objectId}) => {
        return [
          {type: 'Restrictions', id: 'LIST'},
          ...(objectId ? [{type: 'ObjectRestrictions' as const, id: objectId}] : [])
        ]
      },
      async onQueryStarted({objectId, objectType, ...patch}, {dispatch, queryFulfilled, getState}) {
        try {
          const result = (await queryFulfilled).data

          if (isActive(new Date().getTime(), adjustFromTimeZoneSafely(result.startDttm)?.getTime(), adjustFromTimeZoneSafely(result.endDttm)?.getTime())) {
            const queries = containerLevelApi.util.selectInvalidatedBy(getState(), [{type: "ContainerStatuses"}])
            for (const query of queries) {
              const {endpointName, originalArgs} = query
              if (endpointName === 'getAllContainerStatuses') {
                dispatch(
                  containerLevelApi.util.updateQueryData('getAllContainerStatuses', originalArgs, (draft) => {
                    const statusIndexes = objectId ? indexesOf(draft, s => s.containerId === objectId) : indexesOf(draft, s => true)
                    if (statusIndexes) {
                      for (const statusIndex of statusIndexes) {
                        const status = draft[statusIndex]
                        if (status.restrictions) {
                          status.restrictions.push(result)
                        } else {
                          status.restrictions = [result]
                        }
                      }
                    }
                  })
                )
              }
            }
          }
        } catch (error) {
          console.debug(error)
        }
      },
    }),
    updateRestriction: builder.mutation<Restriction, Partial<Restriction>>({
      query: (restriction) => ({
        url: '/restrictions',
        method: 'PUT',
        body: restriction,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Restrictions', id}],
      async onQueryStarted({objectId, objectType, ...patch}, {dispatch, queryFulfilled, getState}) {
        try {
          const result = (await queryFulfilled).data

          const active = isActive(new Date().getTime(), adjustFromTimeZoneSafely(result.startDttm)?.getTime(), adjustFromTimeZoneSafely(result.endDttm)?.getTime())

          const queries = containerLevelApi.util.selectInvalidatedBy(getState(), [{type: "ContainerStatuses"}])
          for (const query of queries) {
            const {endpointName, originalArgs} = query
            if (endpointName === 'getAllContainerStatuses') {
              dispatch(
                containerLevelApi.util.updateQueryData('getAllContainerStatuses', originalArgs, (draft) => {
                  const statusIndexes = objectId ? indexesOf(draft, s => s.containerId === objectId) : indexesOf(draft, s => true)
                  if (statusIndexes) {
                    for (const statusIndex of statusIndexes) {
                      const status = draft[statusIndex]

                      if (active) {
                        if (status.restrictions) {
                          status.restrictions.push(result)
                        } else {
                          status.restrictions = [result]
                        }
                      } else if (status.restrictions) {
                        status.restrictions = status.restrictions.filter((r) => r.id !== result.id)
                      }
                    }
                  }
                })
              )
            }
          }
        } catch (error) {
          console.debug(error)
        }
      },
    }),
    deleteRestriction: builder.mutation<void, Partial<Restriction>>({
      query: (restriction) => ({
        url: '/restrictions',
        method: 'DELETE',
        body: restriction,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Restrictions', id}],
      async onQueryStarted({id, objectId, objectType, ...patch}, {dispatch, queryFulfilled, getState}) {
        try {
          await queryFulfilled

          const queries = containerLevelApi.util.selectInvalidatedBy(getState(), [{type: "ContainerStatuses"}])
          for (const query of queries) {
            const {endpointName, originalArgs} = query
            if (endpointName === 'getAllContainerStatuses') {
              dispatch(
                containerLevelApi.util.updateQueryData('getAllContainerStatuses', originalArgs, (draft) => {
                  let statusIndexes: number[] | undefined = undefined;
                  if (objectId) {
                    statusIndexes = indexesOf(draft, s => s.containerId === objectId)
                  } else {
                    statusIndexes = indexesOf(draft, s => true)
                  }
                  if (statusIndexes) {
                    for (const statusIndex of statusIndexes) {
                      const status = draft[statusIndex]

                      if (status.restrictions) {
                        status.restrictions = status.restrictions.filter((r) => r.id !== id)
                      }
                    }
                  }
                })
              )
            }
          }
        } catch (error) {
          console.debug(error)
        }
      },
    }),
  }),
})

export const {
  useSearchRestrictionsQuery,
  useGetAllRestrictionsQuery,
  useGetRestrictionsByObjectQuery,
  useGetRestrictionQuery,
  useCreateRestrictionMutation,
  useUpdateRestrictionMutation,
  useDeleteRestrictionMutation,
} = restrictionApi;
